import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Building2,
  Calendar,
  AlertCircle,
  MessageSquare,
  Loader2
} from 'lucide-react';

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commenting, setCommenting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      const res = await api.get(`/requests/${id}`);
      setRequest(res.data);
    } catch (error) {
      toast.error('Failed to load request');
      navigate('/requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!request.requester?.id) {
      toast.error('Cannot approve: requester information missing');
      return;
    }

    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('approval_contact_id', request.requester.id);
      formData.append('comments', 'Approved');

      await api.post(`/requests/${id}/approve`, formData);
      toast.success('Request approved');
      fetchRequest();
    } catch (error) {
      toast.error('Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    const reason = prompt('Please provide a reason for rejection:');
    if (!reason) return;

    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('approval_contact_id', request.requester.id);
      formData.append('reason', reason);

      await api.post(`/requests/${id}/reject`, formData);
      toast.success('Request rejected');
      fetchRequest();
    } catch (error) {
      toast.error('Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('status', newStatus);

      await api.put(`/requests/${id}/status`, formData);
      toast.success(`Status updated to ${newStatus}`);
      fetchRequest();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setCommenting(true);
    try {
      const formData = new FormData();
      formData.append('comment', newComment);
      formData.append('is_internal', 'false');

      await api.post(`/requests/${id}/comments`, formData);
      setNewComment('');
      toast.success('Comment added');
      fetchRequest();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setCommenting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_approval: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Approval' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: AlertCircle, label: 'In Progress' },
      completed: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle, label: 'Completed' }
    };

    const config = statusConfig[status] || statusConfig.pending_approval;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/requests')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Requests
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{request.title}</h1>
            <p className="text-sm text-slate-500 mt-1">Request ID: {request.id}</p>
          </div>
          {getStatusBadge(request.status)}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        {request.status === 'pending_approval' && (
          <>
            <Button
              onClick={handleApprove}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </>
        )}
        {request.status === 'approved' && (
          <Button
            onClick={() => handleUpdateStatus('in_progress')}
            disabled={actionLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Start Working
          </Button>
        )}
        {request.status === 'in_progress' && (
          <Button
            onClick={() => handleUpdateStatus('completed')}
            disabled={actionLoading}
            className="bg-green-600 hover:bg-green-700"
          >
            Mark Complete
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap">{request.description}</p>
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Comments ({request.comments?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-4">
                {request.comments?.map((comment, index) => (
                  <div key={comment.id || index} className="border-l-2 border-blue-200 pl-4 py-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{comment.commenter_name}</span>
                      <span className="text-xs text-slate-400">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700">{comment.comment}</p>
                  </div>
                ))}
              </div>

              <form onSubmit={handleAddComment}>
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                />
                <Button
                  type="submit"
                  disabled={commenting || !newComment.trim()}
                  size="sm"
                  className="mt-2"
                >
                  {commenting ? 'Adding...' : 'Add Comment'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <label className="text-slate-500 block mb-1">Requested By</label>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="font-medium">{request.requester?.name}</span>
                </div>
                <div className="text-xs text-slate-400 ml-6">{request.requester?.email}</div>
              </div>

              <div>
                <label className="text-slate-500 block mb-1">Client</label>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="font-medium">{request.client?.company_name}</span>
                </div>
              </div>

              <div>
                <label className="text-slate-500 block mb-1">Request Type</label>
                <span className="capitalize">{request.request_type?.replace('_', ' ')}</span>
              </div>

              <div>
                <label className="text-slate-500 block mb-1">Priority</label>
                <span className="uppercase font-medium">{request.priority}</span>
              </div>

              <div>
                <label className="text-slate-500 block mb-1">Created</label>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span>{new Date(request.created_at).toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Approvals */}
          {request.approvals && request.approvals.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Approvals</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {request.approvals.map((approval, index) => (
                    <div key={approval.id || index} className="text-sm">
                      <div className="flex items-center justify-between">
                        <span>{approval.approver_email}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          approval.status === 'approved' ? 'bg-green-100 text-green-700' :
                          approval.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {approval.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestDetail;
